import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import NavigateBackButton from "app/components/NavigateBackButton";
import { Card } from "react-bootstrap";
import { getS } from "utils/helperFunctions";
import TableSearchInput from "app/components/TableSearchInput";
import TableEmptyState from "app/components/TableEmptyState";
import {
  WeeklyCheckInEntityTypes,
  WeeklyCheckInInitialEntityType,
  OverallActivityInitialTimeInterval,
  OverallActivityTimeIntervals,
  NA_IMPORTANT_COMPANY_ACCOUNTS,
} from "../constants";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getOverallActivityTableData,
  selectDepartments,
  selectGetOverallActivityTableDataStatus,
  selectAllOverallActivityTableData,
  selectAiFeaturesEnabled,
} from "../slice";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import SortableTable from "app/components/SortableTable";
import {
  selectAllCompanyUsersById,
  selectCompanyInfo,
  selectConfigCatFlag,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { OverallActivityTableRow, SelectedActivityRow } from "../types";
import { trackCSVExport } from "utils/trackingFunctions";
import {
  getPageCountWarningMessageText,
  getLoginCountWarningMessageText,
  getOutOfFiveColor,
} from "app/containers/Dashboard/helpers";
import { TableHeader } from "app/components/SortableTable/types";
import { useNavigate } from "react-router-dom";
import {
  getOverallActivityTableDataFromSelectedRow,
  getNameCSVCellValue,
  getCompanyName,
} from "../helpers";
import { selectChildCompanies } from "app/containers/Hierarchy/slice";
import RecordDropdown from "app/components/Dropdowns/RecordDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SimplePopover from "app/components/Popovers/SimplePopover";
import { IS_AU_INSTANCE } from "utils/environmentVariables";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";

interface Props {
  companyAccountId?: string;
  companySelector?: ReactNode;
}

const OverallActivityTable = ({ companyAccountId, companySelector }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // ---------------------------- Selectors ----------------------------
  const getOverallActivityTableDataStatus = useAppSelector(
    selectGetOverallActivityTableDataStatus
  );
  const allOverallActivityTableData = useAppSelector(
    selectAllOverallActivityTableData
  );
  const allTeamsById = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const companyInfo = useAppSelector(selectCompanyInfo);
  const childCompanies = useAppSelector(selectChildCompanies);

  // ---------------------------- States ----------------------------
  const [entitySelected, setEntitySelected] = useState(
    WeeklyCheckInInitialEntityType
  );
  const [timeInterval, setTimeInterval] = useState(
    OverallActivityInitialTimeInterval
  );
  const [displayedInput, setDisplayedInput] = useState<string>("");
  const [breakdownRowSelectedArray, setBreakdownRowSelectedArray] = useState<
    SelectedActivityRow[]
  >([]);
  const breakdownRowSelected = useMemo(
    () => breakdownRowSelectedArray[0] ?? null,
    [breakdownRowSelectedArray]
  ) as SelectedActivityRow;

  // ---------------------------- Effect ----------------------------

  useEffect(() => {
    dispatch(
      getOverallActivityTableData({
        entityType: entitySelected,
        timeInterval,
        companyAccountId,
      })
    );
  }, [dispatch, timeInterval, entitySelected, companyAccountId]);

  // ---------------------------- Handler Functions ----------------------------
  const onSetBreakdownState = (entityId: number, entityType: string) => {
    if (entityType === "user") {
      return;
    }

    if (entityType === "team") {
      const team = allTeamsById[entityId];
      const userAccountIds = team?.teamMemberIds ?? [];
      setEntitySelected("user");
      dispatch(
        getOverallActivityTableData({ entityType: "user", timeInterval, companyAccountId })
      );
      return setBreakdownRowSelectedArray([
        {
          type: "team",
          teamId: entityId,
          userAccountIds,
        },
        ...breakdownRowSelectedArray,
      ]);
    }

    setEntitySelected("team");
    dispatch(getOverallActivityTableData({ entityType: "team", timeInterval, companyAccountId }));
    const teamIds = departments[entityId]?.teams ?? [];
    const userAccountIds = teamIds
      .map((teamId) => {
        const team = allTeamsById[teamId];
        return team?.teamMemberIds ?? [];
      })
      .flat();

    setBreakdownRowSelectedArray([
      {
        type: "department",
        departmentId: entityId,
        teamIds,
        userAccountIds,
      },
      ...breakdownRowSelectedArray,
    ]);
  };

  // ---------------------------- Getter Helper Functions ----------------------------

  const overallActivityTableData = useMemo(() => {
    if (breakdownRowSelected) {
      return getOverallActivityTableDataFromSelectedRow(
        allOverallActivityTableData,
        breakdownRowSelected,
        timeInterval,
        entitySelected
      );
    }

    return Object.values(
      allOverallActivityTableData?.[timeInterval]?.[entitySelected] ?? {}
    );
  }, [
    breakdownRowSelected,
    allOverallActivityTableData,
    entitySelected,
    timeInterval,
  ]);

  const getFilteredRows = useCallback((): OverallActivityTableRow[] => {
    // First we sort the rows by date and then by user name
    const rows = [...overallActivityTableData];

    // If there is no input, return all rows
    if (!displayedInput) {
      return overallActivityTableData;
    }

    if (entitySelected === "team") {
      return rows.filter((row) => {
        const team = allTeamsById[row.entityId];
        if (!team) {
          return false;
        }
        return team.teamName
          .toLowerCase()
          .includes(displayedInput.toLowerCase());
      });
    }

    if (entitySelected === "department") {
      return rows.filter((row) => {
        const department = departments[row.entityId];
        if (!department) {
          return false;
        }
        return department.name
          ?.toLowerCase()
          .includes(displayedInput.toLowerCase());
      });
    }

    // Otherwise, filter the rows by user name or email
    return rows.filter((row) => {
      const user = usersById[row.entityId];
      const firstName = user?.firstName ?? row.firstName ?? "";
      const lastName = user?.lastName ?? row.lastName ?? "";
      const emailAddress = user?.emailAddress ?? row.emailAddress ?? "";
      if (!firstName && !lastName && !emailAddress) {
        return false;
      }
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        fullName.toLowerCase().includes(displayedInput.toLowerCase()) ||
        emailAddress.toLowerCase().includes(displayedInput.toLowerCase())
      );
    });
  }, [
    overallActivityTableData,
    usersById,
    displayedInput,
    entitySelected,
    departments,
    allTeamsById,
  ]);

  const getSearchPlaceholder = () => {
    if (entitySelected === "department") {
      return "Search by department";
    } else if (entitySelected === "team") {
      return "Search by team";
    }
    return "Search by user";
  };

  const getEntityDisplayValue = (
    title: {
      label: string;
      onClick?: () => void;
    },
    description?: string
  ) => (
    <div>
      <div className="user-name-cell">
        {title.onClick ? (
          <button className="button-link" onClick={title.onClick}>
            <p className="user-name-cell__name">{title.label}</p>
          </button>
        ) : (
          <p className="user-name-cell__name">{title.label}</p>
        )}
        {description ? (
          <p className="user-name-cell__job-title">{description}</p>
        ) : null}
      </div>
    </div>
  );

  const getUserCell = (row: OverallActivityTableRow) => {
    const { entityId: userAccountId, firstName: rowFirstName = "", lastName: rowLastName = "", emailAddress: rowEmail = "", jobTitle: rowJobTitle, companyAccountId: rowCompanyId } = row;
    const user = usersById[userAccountId];
    const firstName = user?.firstName ?? rowFirstName;
    const lastName = user?.lastName ?? rowLastName;
    const emailAddress = user?.emailAddress ?? rowEmail;
    if (!firstName && !lastName && !emailAddress) {
      return "";
    }
    const jobTitle = user?.jobTitle || rowJobTitle || "No job title";
    const fullName = `${firstName} ${lastName}`.trim() || emailAddress;
    const isParentCompanyUser = !rowCompanyId || rowCompanyId === companyInfo?.companyAccountId;
    return {
      sortValue: fullName,
      displayValue: getEntityDisplayValue(
        {
          label: fullName,
          onClick: isParentCompanyUser
            ? () => {
                navigate(`/UserGuide/${userAccountId}`);
              }
            : undefined,
        },
        jobTitle
      ),
    };
  };

  const getDepartmentCell = (departmentId: number) => {
    const department = departments[departmentId] ?? {};
    const userAccountIds = department.teamMembers ?? [];
    const memberCount = userAccountIds?.length ?? 0;
    const entityDescription = `${memberCount} member${getS(memberCount)}`;

    const { name = "" } = department;

    return {
      sortValue: name,
      displayValue: getEntityDisplayValue(
        {
          label: name,
          onClick: () => {
            onSetBreakdownState(departmentId, "department");
          },
        },
        entityDescription
      ),
    };
  };

  const getTeamCell = (teamId: number) => {
    const team = allTeamsById[teamId] ?? {};
    const userAccountIds = team.teamMemberIds ?? [];
    const memberCount = userAccountIds?.length ?? 0;
    const entityDescription = `${memberCount} member${getS(memberCount)}`;
    const { teamName = "" } = team;
    return {
      sortValue: teamName,
      displayValue: getEntityDisplayValue(
        {
          label: teamName,
          onClick: () => {
            onSetBreakdownState(teamId, "team");
          },
        },
        entityDescription
      ),
    };
  };

  const getMembersWithMoreThanOneChatCell = (
    memberCount: number,
    peopleWithMoreThanOneChatCount: number = 0
  ) => {
    const percentage = memberCount
      ? (peopleWithMoreThanOneChatCount / memberCount) * 100
      : 0;
    const percentageString = percentage.toFixed() + "%";
    const dotColor = getOutOfFiveColor(percentage / 20);
    return {
      sortValue: percentageString,
      displayValue: (
        <div className="row-gap-8px align-items-center">
          <p>{percentageString}</p>
          <div className={`status-dot ${dotColor}`} />
        </div>
      ),
    };
  };

  const getTableRow = (row: OverallActivityTableRow) => {
    const {
      entityId,
      peopleWithMoreThanOneChatCount,
      logonCount,
      coachBoChatCount,
      weeklyCheckInCount,
      completedTeamScanCount,
      pageViewCount = 0,
      eppCompleted,
      totalTeamScanCount,
      companyAccountId: rowCompanyAccountId,
    } = row;
    const company = companySelector
      ? getCompanyName(rowCompanyAccountId, companyInfo, childCompanies)
      : undefined;
    if (entitySelected === "user") {
      return {
        logonCount,
        coachBoChatCount,
        weeklyCheckInCount,
        user: getUserCell(row),
        company,
        pageViewCount,
        eppCompleted: eppCompleted ? "Yes" : "No",
        totalTeamScanCount,
      };
    }

    if (entitySelected === "team") {
      const team = allTeamsById[entityId];
      const memberCount = team?.teamMemberIds?.length ?? 0;
      const peopleWithMoreThanOneChatCountCell =
        getMembersWithMoreThanOneChatCell(
          memberCount,
          peopleWithMoreThanOneChatCount
        );
      return {
        logonCount,
        coachBoChatCount,
        weeklyCheckInCount,
        completedTeamScanCount,
        team: getTeamCell(entityId),
        company,
        peopleWithMoreThanOneChatCount: peopleWithMoreThanOneChatCountCell,
        pageViewCount,
      };
    }

    const department = departments[entityId];
    const memberCount = department?.teamMembers?.length ?? 0;
    const peopleWithMoreThanOneChatCountCell =
      getMembersWithMoreThanOneChatCell(
        memberCount,
        peopleWithMoreThanOneChatCount
      );
    return {
      logonCount,
      coachBoChatCount,
      weeklyCheckInCount,
      completedTeamScanCount,
      department: getDepartmentCell(entityId),
      company,
      peopleWithMoreThanOneChatCount: peopleWithMoreThanOneChatCountCell,
      pageViewCount,
    };
  };


  const getCSVExportRows = useCallback(() => {
    trackCSVExport("Overall Activity");
    return getFilteredRows().map(
      (row) => {
        const {
          entityId,
          logonCount,
          coachBoChatCount,
          peopleWithMoreThanOneChatCount = 0,
          completedTeamScanCount = 0,
          weeklyCheckInCount = 0,
          pageViewCount = 0,
          eppCompleted,
          totalTeamScanCount = 0,
        } = row;
        let memberCount = 0;

        if (entitySelected === "team") {
          memberCount = allTeamsById[entityId]?.teamMemberIds?.length ?? 0;
        } else if (entitySelected === "department") {
          memberCount = departments[entityId]?.teamMembers?.length ?? 0;
        }

        const percentage = memberCount
          ? (peopleWithMoreThanOneChatCount / memberCount) * 100
          : 0;

        return {
          [entitySelected]: getNameCSVCellValue(
            row.entityId,
            entitySelected,
            { departments, teamsById: allTeamsById, usersById },
            { firstName: row.firstName, lastName: row.lastName }
          ),
          logonCount: Number(logonCount),
          coachBoChatCount,
          peopleWithMoreThanOneChatCount: percentage.toFixed() + "%",
          completedTeamScanCount,
          weeklyCheckInCount,
          pageViewCount,
          eppCompleted: eppCompleted ? "Yes" : "No",
          totalTeamScanCount,
        };
      }
    );
  }, [
    getFilteredRows,
    entitySelected,
    departments,
    allTeamsById,
    usersById,
  ]);

  const getHeaders = () => {
    const isAllianz =
      !IS_AU_INSTANCE &&
      NA_IMPORTANT_COMPANY_ACCOUNTS.Allianz === companyInfo?.companyAccountId;
    const nameHeaderLabel =
      entitySelected.charAt(0).toUpperCase() + entitySelected.slice(1);
    const mainHeaders: TableHeader[] = [
      {
        label: nameHeaderLabel,
        key: entitySelected,
        className: "entity-header",
      },
      ...(companySelector
        ? [{ label: "Company", key: "company" }]
        : []),
      {
        label: "Logins",
        key: "logonCount",
        sortInverse: true,
        additionalContent: !isAllianz ? (
          <SimplePopover
            popoverContent={
              <div>
                <p>{getLoginCountWarningMessageText()}</p>
              </div>
            }
            trigger="hover"
            popoverTitle=""
            popoverClassName="checkin-score-popover"
            hideTitleAndCloseButton
          >
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              style={{ paddingLeft: "5px" }}
            />
          </SimplePopover>
        ) : null,
      },
      {
        label: "Page Views",
        key: "pageViewCount",
        sortInverse: true,
        additionalContent: (
          <SimplePopover
            popoverContent={
              <div>
                <p>{getPageCountWarningMessageText()}</p>
              </div>
            }
            trigger="hover"
            popoverTitle=""
            popoverClassName="checkin-score-popover"
            hideTitleAndCloseButton
          >
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              style={{ paddingLeft: "5px" }}
            />
          </SimplePopover>
        ),
      },
      { label: "Coach Bo Chats", key: "coachBoChatCount", sortInverse: true },
    ];

    if (weeklyCheckInConfigCatFlag && areAIFeaturesEnabled) {
      mainHeaders.splice(3, 0, {
        label: `Manager Check-ins`,
        key: "weeklyCheckInCount",
        sortInverse: true,
      });
    }

    if (entitySelected !== "user") {
      mainHeaders.push(
        {
          label: "TEAMscans Completed",
          key: "completedTeamScanCount",
          sortInverse: true,
        },
        {
          label: "% People With 1+ Coach Bo Chats",
          key: "peopleWithMoreThanOneChatCount",
          disableSort: true,
        }
      );
    } else {
      mainHeaders.push(
        { label: "EPP Connected", key: "eppCompleted", sortInverse: true },
        {
          label: "TEAMscans Completed",
          key: "totalTeamScanCount",
          sortInverse: true,
        }
      );
    }

    return mainHeaders;
  };

  const onBackNavigate = () => {
    const newBreakdownRowSelectedArray = [...breakdownRowSelectedArray];
    const removedSelectedRow = newBreakdownRowSelectedArray.shift();

    if (removedSelectedRow?.type) {
      setEntitySelected(removedSelectedRow?.type);
    }
    setBreakdownRowSelectedArray(newBreakdownRowSelectedArray);
  };

  const getEntitySelectedText = () => {
    if (!breakdownRowSelected) {
      return "";
    }

    if (breakdownRowSelected.type === "department") {
      const departmentName =
        departments[breakdownRowSelected.departmentId]?.name ?? "";
      return departmentName.toLowerCase().includes("department")
        ? departmentName
        : `${departmentName} Department`;
    }

    const teamName = allTeamsById[breakdownRowSelected.teamId]?.teamName;
    return teamName.toLowerCase().includes("team")
      ? teamName
      : `${teamName} Team`;
  };

  const getTitleSection = () => {
    if (!breakdownRowSelected) {
      return (
        <div className="d-flex justify-content-between align-items-center">
          <div className="column-gap-8px">
            <h2>Overall Activity</h2>
            <p>Showing activity in this entire organization</p>
          </div>
          {companySelector}
        </div>
      );
    }

    const entitySelectedText = getEntitySelectedText();
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="column-gap-8px">
          <h2>Overall Activity Breakdown</h2>
          <div className="row-gap-4px grey-text">
            <p>
              Overall activity for {entitySelectedText} from{" "}
              {OverallActivityTimeIntervals[timeInterval]}
            </p>
          </div>
        </div>
        <ExportCSVButton
          headers={headers.filter((header) => header.key !== "dropdown")}
          getRows={getCSVExportRows}
        />
      </div>
    );
  };

  const getDropdownSection = () => {
    if (breakdownRowSelected?.type === "department") {
      return (
        <RecordDropdown
          record={{
            user: WeeklyCheckInEntityTypes.user,
            team: WeeklyCheckInEntityTypes.team,
          }}
          selected={entitySelected}
          onSelect={(e) => {
            setEntitySelected(e ?? "user");
          }}
          isDisabled={isDropdownDisabled}
        />
      );
    }

    if (breakdownRowSelected) {
      return null;
    }

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="row-gap-12px">
          <RecordDropdown
            record={OverallActivityTimeIntervals}
            selected={timeInterval}
            onSelect={(e) => {
              setTimeInterval(e ?? OverallActivityInitialTimeInterval);
              setDisplayedInput("");
            }}
            isDisabled={isDropdownDisabled}
          />
          <RecordDropdown
            record={WeeklyCheckInEntityTypes}
            selected={entitySelected}
            onSelect={(e) => {
              setEntitySelected(e ?? "user");
              setDisplayedInput("");
            }}
            isDisabled={isDropdownDisabled}
          />
        </div>
        <div>
          <ExportCSVButton
            headers={headers.filter((header) => header.key !== "dropdown")}
            getRows={getCSVExportRows}
            fileName="Overall Activity"
          />
        </div>
      </div>
    );
  };

  const isDropdownDisabled = getOverallActivityTableDataStatus === "loading";
  const headers = getHeaders();
  const rows = getFilteredRows().map(getTableRow);

  return (
    <>
      {breakdownRowSelected && (
        <div style={{ marginBottom: "20px" }}>
          <NavigateBackButton onClick={onBackNavigate} />
        </div>
      )}
      <Card>
        {getTitleSection()}
        <TableSearchInput
          value={displayedInput}
          onChange={setDisplayedInput}
          placeholder={getSearchPlaceholder()}
        />
        {getDropdownSection()}
        <SortableTable
          rows={rows}
          tableClassName="admin-console-table"
          columnHeaders={[...headers]}
          isLoading={getOverallActivityTableDataStatus === "loading"}
        />
        <TableEmptyState
          isLoading={getOverallActivityTableDataStatus === "loading"}
          totalDataCount={overallActivityTableData.length}
          filteredCount={rows.length}
          noDataTitle="No overall activity found"
          noDataBody="Once there is overall activity, it will be shown here."
          entitySelected={entitySelected}
          searchInput={displayedInput}
        />
      </Card>
    </>
  );
};

export default OverallActivityTable;
